import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DjRoomPerks } from "@/components/DjRoomPerks";

const fadeInUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export const RoomsShowcase = () => (
  <>
      {/* Studio Spaces Showcase — Recording Studio + DJ Room */}
      <section id="rooms" className="py-24 md:py-32 bg-dream-surface overflow-hidden scroll-mt-20" data-testid="studio-showcase">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mb-14 max-w-3xl"
          >
            <p className="text-dream-violet font-mono text-xs tracking-[0.3em] mb-4 uppercase">Step Inside</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
              The Studio &amp; DJ Room.
            </h2>
            <p className="text-white/55 text-base md:text-lg">
              Two purpose-built rooms in Tullamarine, Melbourne — professionally treated, industry-grade gear, ready when you are.
            </p>
          </motion.div>

          {/* Recording Studio Block */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mb-16"
            data-testid="showcase-recording"
          >
            <div className="grid lg:grid-cols-3 gap-4 lg:gap-6 items-stretch">
              <div className="lg:col-span-2 relative rounded-3xl overflow-hidden border border-white/10 min-h-[320px] lg:min-h-[440px] group">
                <img
                  src="https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/wdkege0p_DSC_0076.jpg"
                  alt="Recording Studio Control Room"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-dream-violet font-mono text-xs uppercase tracking-widest mb-2">Recording Studio</p>
                  <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">Where the magic gets tracked.</h3>
                  <p className="text-white/70 max-w-xl mb-5 text-sm md:text-base">
                    Professionally treated live room, mic locker, and control room built around Focal monitors — engineered for vocals, bands, and full productions.
                  </p>
                  <Link to="/booking?service=package_dmrg_studio">
                    <Button data-testid="rooms-book-studio-btn" className="bg-dream-violet hover:bg-dream-violet-glow text-white rounded-full px-6 font-semibold">
                      Book Studio Time <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 lg:gap-6">
                {[
                  { src: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/0epweuqa_DSC_0085.jpg", alt: "Recording Booth Microphone" },
                  { src: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/p2m69fbe_DSC_0086.jpg", alt: "Live Room Drum Kit" },
                ].map((p) => (
                  <div key={p.src} className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/3] lg:aspect-auto lg:min-h-[210px] group">
                    <img
                      src={p.src}
                      alt={p.alt}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* DJ Room Block */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            data-testid="showcase-djroom"
          >
            <div className="grid lg:grid-cols-3 gap-4 lg:gap-6 items-stretch">
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 lg:gap-6 lg:order-first order-last">
                {[
                  { src: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/9c2fw01q_DSC_2674.jpg", alt: "DJ Setup" },
                  { src: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/irjscrt2_DSC_0145.jpg", alt: "DJ Room Lounge" },
                ].map((p) => (
                  <div key={p.src} className="relative rounded-2xl overflow-hidden border border-cyan-500/20 aspect-[4/3] lg:aspect-auto lg:min-h-[210px] group">
                    <img
                      src={p.src}
                      alt={p.alt}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                ))}
              </div>
              <div className="lg:col-span-2 relative rounded-3xl overflow-hidden border border-cyan-500/25 min-h-[320px] lg:min-h-[440px] group">
                <img
                  src="https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/o4aghb0q_DSC_2699.jpg"
                  alt="DJ Room Decks"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-cyan-300 font-mono text-xs uppercase tracking-widest mb-2">DJ Room · Dream Maker Sessions</p>
                  <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">Melbourne&apos;s cheapest pro DJ room.</h3>
                  <p className="text-white/70 max-w-xl mb-5 text-sm md:text-base">
                    3× Pioneer CDJ-3000s and a DJM-A9 in a fully sound-treated room. Members get priority access &amp; monthly hours — book by the hour, package, or membership.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/booking?service=package_dj_popular">
                      <Button data-testid="rooms-book-dj-btn" className="bg-cyan-500 hover:bg-cyan-400 text-white rounded-full px-6 font-semibold">
                        Book DJ Room <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <Link to="/dj-memberships">
                      <Button variant="outline" className="border-white/30 text-white hover:border-cyan-400 hover:text-cyan-300 bg-transparent rounded-full px-6 font-semibold">
                        View Memberships
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <DjRoomPerks className="mt-8" testId="home-dj-room-perks" />

          {/* Gear strip */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
            data-testid="showcase-gear-strip"
          >
            {[
              { label: "Focal Monitors" },
              { label: "Pioneer CDJ-3000s" },
              { label: "Pioneer DJM-A9" },
              { label: "Sound-Treated Rooms" },
            ].map((g) => (
              <div key={g.label} className="px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 text-center">
                <span className="text-white/85 text-sm font-medium">{g.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

  </>
);
